import React from 'react';
import { Button, Input, Popconfirm, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

/**
 * UI 草稿层使用的 AOI 门禁 code 形态 - 跟 biz_aoi_attribute.access_codes 的元素一一对应。
 *
 * <p>没有 id / addressId: 门禁归属 AOI 本身 (社区大门 / 楼门 / 电梯 密码), 不挂具体地址;
 * 保存时整个列表随 upsertAoiAttribute 全量覆盖, 元素身份由服务端重算的 uid 表达,
 * 所以前端既不做 diff, 也不需要维护主键。
 */
export interface AccessCodeDraft {
    codeType: string;
    codeValue: string;
    /** DB 枚举名: PENDING_VERIFICATION / AVAILABLE / UNAVAILABLE */
    available: string;
    remark?: string | null;
}

/** 一个空白新条目 (用户点 + 新增 时插入)。 */
export function emptyAccessCodeDraft(): AccessCodeDraft {
    return {
        codeType: '',
        codeValue: '',
        available: 'PENDING_VERIFICATION',
        remark: null,
    };
}

/** 门禁类型下拉选项 (值 = 落库 codeType)。 */
export const ACCESS_CODE_TYPE_OPTIONS = [
    { value: 'Gate', label: '社区门禁' },
    { value: 'MainDoor', label: '楼门门禁' },
    { value: 'SideDoor', label: '侧门门禁' },
    { value: 'Elevate', label: '电梯门禁' },
    { value: 'Garage', label: '车库门禁' },
    { value: 'ParcelStop', label: '快递柜' },
];

/** 门禁状态下拉选项 (值 = 落库 available, DB 枚举名)。 */
export const ACCESS_CODE_STATUS_OPTIONS = [
    { value: 'PENDING_VERIFICATION', label: '待验证' },
    { value: 'AVAILABLE', label: '可用' },
    { value: 'UNAVAILABLE', label: '不可用' },
];

interface Props {
    /** 当前草稿列表 (受控)。 */
    codes: AccessCodeDraft[];
    /** 草稿变更回调。 */
    onChange: (next: AccessCodeDraft[]) => void;
}

/**
 * AOI 编辑面板里的门禁 code 子组件 - 行为对齐面板内其他列表字段 (进门点 / 停车点 / 配送时间)。
 *
 * <p>"新增" 按钮由父组件渲染在 Card extra 里, 跟其他字段一致 (本组件不内置 + 新增按钮)。
 */
const AccessCodeEditor: React.FC<Props> = ({ codes, onChange }) => {
    const removeCode = (idx: number) => {
        onChange(codes.filter((_, i) => i !== idx));
    };
    const updateCode = (idx: number, patch: Partial<AccessCodeDraft>) => {
        onChange(codes.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };

    if (codes.length === 0) {
        return <span className="text-xs text-gray-400">无</span>;
    }

    return (
        <div className="space-y-2">
            {codes.map((c, idx) => (
                <CodeRow
                    key={idx}
                    code={c}
                    onChange={(patch) => updateCode(idx, patch)}
                    onRemove={() => removeCode(idx)}
                />
            ))}
        </div>
    );
};

/* ====================== 子组件 ====================== */

const CodeRow: React.FC<{
    code: AccessCodeDraft;
    onChange: (patch: Partial<AccessCodeDraft>) => void;
    onRemove: () => void;
}> = ({ code, onChange, onRemove }) => {
    return (
        <div className="p-2 border rounded space-y-1 bg-gray-50">
            <div className="flex items-center justify-end">
                <Popconfirm
                    title="确认删除该门禁 code?"
                    okText="确定"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={onRemove}
                >
                    <Button
                        size="small"
                        danger
                        type="link"
                        icon={<DeleteOutlined />}
                    >删除</Button>
                </Popconfirm>
            </div>
            <div className="flex gap-2">
                <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">类型</div>
                    <Select
                        className="w-full"
                        size="small"
                        placeholder="类型"
                        value={code.codeType || undefined}
                        onChange={(v) => onChange({ codeType: v || '' })}
                        allowClear
                        options={ACCESS_CODE_TYPE_OPTIONS}
                    />
                </div>
                <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">状态</div>
                    <Select
                        className="w-full"
                        size="small"
                        value={code.available}
                        onChange={(v) => onChange({ available: v })}
                        options={ACCESS_CODE_STATUS_OPTIONS}
                    />
                </div>
            </div>
            <div>
                <div className="text-xs text-gray-500 mb-1">Code 值</div>
                <Input
                    size="small"
                    placeholder="如: 1234#"
                    value={code.codeValue}
                    onChange={(e) => onChange({ codeValue: e.target.value })}
                    maxLength={64}
                />
            </div>
            <div>
                <div className="text-xs text-gray-500 mb-1">备注</div>
                <Input
                    size="small"
                    placeholder="可空"
                    value={code.remark ?? ''}
                    onChange={(e) => onChange({ remark: e.target.value || null })}
                    maxLength={255}
                />
            </div>
        </div>
    );
};

export default AccessCodeEditor;
